import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';
import { AppStoreBadge } from './brand.js';
import { Button } from './controls.js';
import { around, t } from './i18n/index.js';
import { IconCamera, IconChevronRight, IconQrCode, IconRefresh, IconShield, IconSmartphone } from './icons.js';

/** The waiting room, or null once its code ran out. */
type Waiting = { code: string | null; link: string; expiresAt: number };

export interface PairingProps {
  pairing: Waiting | null;
  /** Why there is no code (when `pairing` is null): it ran out, or the relay went away. */
  stop?: 'expired' | 'reconnecting' | 'offline';
  /** An iPhone typed the code and waits for this page to let it in. */
  confirm?: boolean;
  renewing?: boolean;
  onExpire: () => void;
  onRenew: () => void;
  onAllow: () => void;
  onReject: () => void;
}

/** The code's life on the relay, for the bar that runs down under it. */
const TTL_SECONDS = 120;

/**
 * Pairing (variant C of the 2026 review, tellvo-voip-client#96): one card in the middle of the
 * window, the steps on the left, the QR and the code on the right. Waiting, run out, lost and
 * "an iPhone asks to connect" share the right-hand panel: they are layers in one grid cell, so
 * the card never changes size between them.
 */
export function Pairing({ pairing, stop = 'expired', confirm = false, renewing = false, onExpire, onRenew, onAllow, onReject }: PairingProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const left = useCountdown(pairing?.expiresAt ?? null, onExpire);
  // The last code's QR stays on screen, blurred under the panel, once it runs out or the relay
  // goes: the card keeps its look and its size (the mockup's expired state).
  const [shown, setShown] = useState<string | null>(pairing?.link ?? null);
  useEffect(() => { if (pairing?.link) setShown(pairing.link); }, [pairing?.link]);
  useEffect(() => {
    if (canvas.current && shown) void QRCode.toCanvas(canvas.current, shown, { width: 320, margin: 1, errorCorrectionLevel: 'M' });
  }, [shown]);
  const expired = pairing === null && !confirm;
  const code = pairing?.code;
  const digits = (code ?? '').padEnd(6, ' ').slice(0, 6).split('');
  const step2 = around('pair.step2', 'path', <span className="path">{t('pair.step2.path')}</span>);

  return (
    <main className="pair">
      <div className="card pair__card">
        <div className="pair__text">
          <span className="eyebrow">{t('brand.page')}</span>
          <h1>{t('pair.title')}</h1>
          <p className="pair__lead">{t('pair.lead')}</p>
          <ol className="psteps">
            <li><span className="step-n">1</span><b>{t('pair.step1')}</b></li>
            <li><span className="step-n">2</span><b>{step2[0]}{step2[1]}{step2[2]}</b></li>
            <li><span className="step-n">3</span><span><b>{t('pair.step3')}</b><span className="field__hint">{t('pair.step3.hint')}</span></span></li>
          </ol>
          <p className="pair__trust"><IconShield />{t('pair.privacy')}</p>
          <div className="pair__get"><span>{t('pair.get')}</span><AppStoreBadge label={t('pair.appStore')} /></div>
        </div>

        <div className="pair__panel">
          <div className={`pair__layer${confirm ? ' is-off' : ''}`} aria-hidden={confirm || undefined}>
            <div className={`pair__qr${expired ? ' pair__qr--off' : ''}${shown ? '' : ' pair__qr--none'}`}>
              {shown ? <canvas ref={canvas} role="img" aria-label={expired ? undefined : t('pair.qrLabel')} aria-hidden={expired || undefined} /> : <IconQrCode size="lg" />}
              {expired ? (
                <div className="pair__veil" role="status">
                  <strong>
                    {stop === 'reconnecting' ? <span className="spinner" aria-hidden="true" /> : null}
                    {t(stop === 'reconnecting' ? 'pair.lost.title' : stop === 'offline' ? 'pair.offline.title' : 'pair.expired.title')}
                  </strong>
                  <span className="field__hint">{t(stop === 'reconnecting' ? 'pair.lost.text' : stop === 'offline' ? 'pair.offline.text' : 'pair.expired.text')}</span>
                  {/* While it rejoins by itself there is nothing to press. */}
                  {stop === 'reconnecting' ? null : (
                    <Button kind="primary" busy={renewing} onClick={onRenew}>{renewing ? null : <IconRefresh />}{t('pair.renew')}</Button>
                  )}
                </div>
              ) : null}
            </div>
            <span className="pair__cap"><IconCamera size="sm" />{t('pair.qr')}</span>
            <span className="pair__or">{t('pair.or')}</span>
            <div className={`pcode num${pairing ? '' : ' pcode--off'}`} aria-label={pairing && code ? t('pair.codeLabel') + ' ' + code : undefined}>
              {digits.slice(0, 3).map((d, i) => <span key={i}>{d}</span>)}<span className="pcode__gap" aria-hidden="true" />
              {digits.slice(3).map((d, i) => <span key={i + 3}>{d}</span>)}
            </div>
            <div className="timer">
              <div className="timer__bar"><i style={{ transform: `scaleX(${pairing ? left / TTL_SECONDS : 0})` }} /></div>
              {/* The slot keeps its height when the timer is gone, so nothing moves. */}
              <span className="field__hint num">{pairing ? t('pair.timer', { time: clock(left) }) : ' '}</span>
            </div>
          </div>

          <div className={`pair__layer pair__confirm${confirm ? '' : ' is-off'}`} aria-hidden={!confirm || undefined} role={confirm ? 'alertdialog' : undefined} aria-labelledby="confirm-title">
            <span className="tile"><IconSmartphone size="lg" /></span>
            <h2 id="confirm-title">{t('confirm.title')}</h2>
            <div className="note note--warn">{t('confirm.text')}</div>
            <div className="form-foot pair__confirm-foot">
              <Button onClick={onReject}>{t('confirm.reject')}</Button>
              <Button kind="primary" onClick={onAllow}>{t('confirm.allow')}<IconChevronRight size="sm" /></Button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

/** Seconds left until `deadline`, ticking; calls `onZero` once when it runs out. */
function useCountdown(deadline: number | null, onZero: () => void): number {
  const [now, setNow] = useState(() => Date.now());
  const fired = useRef(false);
  useEffect(() => {
    fired.current = false;
    if (deadline === null) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [deadline]);
  const left = deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));
  useEffect(() => {
    if (deadline !== null && left === 0 && !fired.current) {
      fired.current = true;
      onZero();
    }
  }, [deadline, left, onZero]);
  return left;
}

/** 95 → "1:35". */
function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
